// public/v3/src/lib/quick-log-presets.js
// Pure model for Time Logs quick-log activity presets — defaults,
// normalization, settings serialization, and the lens→set selector.
//
// Extracted from time-logs.jsx so the (error-prone) normalization and the
// CCM/BHI family handling are unit-testable. Jest has no ESM transform in this
// repo, so this is CommonJS (esbuild bundles CJS fine); time-logs.jsx imports
// it with `import { ... } from '../lib/quick-log-presets'`.

// CCM activity presets — practice-shared, admin-editable. `inline: true` means
// the chip renders directly on each Time Logs row; `inline: false` puts it in
// the "More…" popover. Array order controls inline ordering left-to-right.
const DEFAULT_PRESETS = [
  { id:'qlp-care-coord', mins:20, label:'Care coord',  type:'Care Coordination',     hint:'Phone/coordination with PCP/specialist', desc:'', inline:true },
  { id:'qlp-caregiver',  mins:15, label:'Caregiver',   type:'Care Coordination',     hint:'Family caregiver check-in',              desc:'Discussion with caregiver', inline:true },
  { id:'qlp-services',   mins:10, label:'Services',    type:'Care Coordination',     hint:'Community services / referrals',         desc:'Working services / referrals', inline:true },
  { id:'qlp-med-pa',     mins:30, label:'Med PA',      type:'Medication Management', hint:'Prior authorization, pharmacy',          desc:'Prior authorization', inline:false },
  { id:'qlp-lab-review', mins:15, label:'Lab review',  type:'Lab/Test Review',       hint:'Labs/imaging review + outreach',         desc:'', inline:false },
  { id:'qlp-care-plan',  mins:10, label:'Plan update', type:'Care Plan Update',      hint:'Care plan revision',                     desc:'', inline:false },
];

// BHI quick-log presets — practice-shared, admin-editable in the BHI lens.
// Surfaces for BHI rows in the BHI lens and All lens. Defaults match the
// activity types audit-relevant for 99484 BHI billing (care coordination,
// caregiver outreach, psychotropic medication review, BH care plan update,
// community psych/SUD service referrals).
const BHI_DEFAULT_PRESETS = [
  { id:'bhi-care-coord', mins:15, label:'BH care coord',  type:'Care Coordination',     hint:'Coordination with psychiatry / therapy',           desc:'', inline:true, programFamily: 'bhi' },
  { id:'bhi-caregiver',  mins:15, label:'Caregiver',      type:'Care Coordination',     hint:'Caregiver discussion re: behavioral health',       desc:'Discussion with caregiver (BH)', inline:true, programFamily: 'bhi' },
  { id:'bhi-med-review', mins:10, label:'Med review',     type:'Medication Management', hint:'Psychotropic medication review',                   desc:'Psychotropic medication review', inline:true, programFamily: 'bhi' },
  { id:'bhi-plan-upd',   mins:10, label:'Plan update',    type:'Care Plan Update',      hint:'BH care plan revision',                            desc:'BH care plan revision', inline:true, programFamily: 'bhi' },
  { id:'bhi-psych-svcs', mins:10, label:'Psych services', type:'Care Coordination',     hint:'Community psychiatric / SUD services / referrals', desc:'Community psych/SUD services', inline:false, programFamily: 'bhi' },
];

// Chronic Pain Management quick-log presets. CPM time is PRACTITIONER/QHP
// time (G3002 is not a clinical-staff code), so the preset hints say so —
// logging staff-performed work under these presets misdocuments the code.
// Shown in the CCM and All lenses; per-patient applicability is gated by
// canLogPresetForPatient (billing.painProgram must be set).
// CPM care-management presets are phone-first. In-person defaults belong on
// visit-type pain presets when those are added.
const PAIN_DEFAULT_PRESETS = [
  { id:'pain-assess',   mins:15, label:'Pain assess',   type:'Care Coordination',     hint:'Practitioner pain assessment / med response review', desc:'Pain assessment and treatment-response review', inline:true,  mode:'telehealth', programFamily: 'pain' },
  { id:'pain-med-mgmt', mins:15, label:'Pain med mgmt', type:'Medication Management', hint:'Practitioner pain-medication management',             desc:'Pain medication management', inline:true,  mode:'telehealth', programFamily: 'pain' },
  { id:'pain-plan-upd', mins:10, label:'Pain plan',     type:'Care Plan Update',      hint:'Person-centered pain care-plan revision',             desc:'Pain care plan revision', inline:false, mode:'telehealth', programFamily: 'pain' },
];

// Principal Illness Navigation presets. PIN is staff-visible auxiliary-
// personnel navigation time, kept in its own family so entries carry G0023
// and never mix into CCM/BHI/CPM minute totals.
const PIN_DEFAULT_PRESETS = [
  { id:'pin-housing-sdoh', mins:15, label:'Housing/SDOH follow-up', type:'Care Coordination', hint:'Follow up on housing, food, utilities, or other social needs', desc:'Housing and SDOH follow-up', inline:true, programFamily: 'pin' },
  { id:'pin-benefits', mins:10, label:'Benefits coordination', type:'Care Coordination', hint:'Coordinate insurance, financial assistance, or public benefits', desc:'Benefits coordination', inline:true, programFamily: 'pin' },
  { id:'pin-appt-referral', mins:10, label:'Appointment & referral navigation', type:'Care Coordination', hint:'Navigate appointments, referrals, and care access', desc:'Appointment and referral navigation', inline:true, programFamily: 'pin' },
  { id:'pin-transport', mins:5, label:'Transport coordination', type:'Care Coordination', hint:'Arrange or confirm transportation for care', desc:'Transport coordination', inline:true, programFamily: 'pin' },
  { id:'pin-community', mins:15, label:'Community services connection', type:'Care Coordination', hint:'Connect patient with community-based services and supports', desc:'Community services connection', inline:false, programFamily: 'pin' },
];

function normalizePreset(p, idx) {
  const raw = p && typeof p === 'object' ? p : {};
  const mins = Number(raw.mins ?? raw.minutes ?? raw.duration) || 0;
  const label = String(raw.label || raw.type || 'Preset').trim().slice(0, 40) || 'Preset';
  const type = String(raw.type || label || 'Other').trim().slice(0, 80) || 'Other';
  const desc = String(raw.desc ?? raw.notes ?? raw.description ?? '').trim().slice(0, 200);
  const hint = String(raw.hint ?? raw.description ?? raw.desc ?? '').trim().slice(0, 200);
  const mode = raw.mode === 'in-person' || raw.mode === 'telehealth' ? raw.mode : undefined;
  return {
    id: String(raw.id || `qlp-${idx || Date.now()}`).trim(),
    mins: Math.max(1, Math.min(240, Math.round(mins || 10))),
    label,
    type,
    hint,
    desc,
    inline: raw.inline !== false,
    ...(mode ? { mode } : {}),
    programFamily: raw.programFamily === 'bhi' ? 'bhi' : raw.programFamily === 'pain' ? 'pain' : raw.programFamily === 'pin' ? 'pin' : 'ccm',
  };
}

function normalizePresets(list) {
  const source = Array.isArray(list) && list.length ? list : DEFAULT_PRESETS;
  return source.map((p, i) => normalizePreset(p, i));
}

function presetsForSettings(list) {
  return normalizePresets(list).map((p) => ({
    ...p,
    minutes: p.mins,
    description: p.desc || p.hint || '',
  }));
}

// BHI variant: never falls back to the CCM defaults (an admin who deletes every
// BHI preset must get BHI defaults back, not CCM ones), and force-stamps
// programFamily:'bhi' so a logged BHI preset always derives the 99484-family
// CPT via cptForPreset (which keys off preset.programFamily).
function normalizeBhiPresets(list) {
  const source = Array.isArray(list) && list.length ? list : BHI_DEFAULT_PRESETS;
  return source.map((p, i) => {
    const raw = p && typeof p === 'object' ? p : {};
    // pre-inject so normalizePreset's ternary returns 'bhi'; outer stamp guards
    // against a future change to that ternary.
    return { ...normalizePreset({ ...raw, programFamily: 'bhi' }, i), programFamily: 'bhi' };
  });
}

function bhiPresetsForSettings(list) {
  return normalizeBhiPresets(list).map((p) => ({
    ...p,
    minutes: p.mins,
    description: p.desc || p.hint || '',
  }));
}

// Pain variant of normalizeBhiPresets — same force-stamp rationale.
function normalizePainPresets(list) {
  const source = Array.isArray(list) && list.length ? list : PAIN_DEFAULT_PRESETS;
  return source.map((p, i) => {
    const raw = p && typeof p === 'object' ? p : {};
    return { ...normalizePreset({ ...raw, programFamily: 'pain' }, i), programFamily: 'pain' };
  });
}

// PIN variant of normalizePainPresets — force-stamps the navigation family.
function normalizePinPresets(list) {
  const source = Array.isArray(list) && list.length ? list : PIN_DEFAULT_PRESETS;
  return source.map((p, i) => {
    const raw = p && typeof p === 'object' ? p : {};
    return { ...normalizePreset({ ...raw, programFamily: 'pin' }, i), programFamily: 'pin' };
  });
}

// Lens → effective preset set. CCM lens shows CCM (+pain) presets; BHI and
// future PIN lenses show their own sets; All interleaves every enabled set.
// Pain rides the CCM view because CPM pairs onto the CCM/APCM bill —
// per-preset programFamily keeps downstream CPT derivation and per-patient
// eligibility correct.
function effectivePresetsFor(programFilter, ccmPresets, bhiPresets, painPresets, pinPresets) {
  const pain = Array.isArray(painPresets) ? painPresets : [];
  const pin = Array.isArray(pinPresets) ? pinPresets : [];
  if (programFilter === 'bhi') return bhiPresets;
  if (programFilter === 'pain') return pain;
  if (programFilter === 'pin') return pin;
  if (programFilter === 'all') return [...ccmPresets, ...bhiPresets, ...pain, ...pin];
  // CCM lens: identity when no pain presets are in play (callers and tests
  // rely on reference equality for memoization checks).
  return pain.length ? [...ccmPresets, ...pain] : ccmPresets;
}

module.exports = {
  DEFAULT_PRESETS,
  BHI_DEFAULT_PRESETS,
  normalizePreset,
  normalizePresets,
  presetsForSettings,
  normalizeBhiPresets,
  bhiPresetsForSettings,
  PAIN_DEFAULT_PRESETS,
  normalizePainPresets,
  PIN_DEFAULT_PRESETS,
  normalizePinPresets,
  effectivePresetsFor,
};
